import BaseModal from "components/BaseModal";
import WizardFormV2 from "components/WizardFormV2";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FieldTypes, editorRules } from "utils/formUtils";
import _, { isEmpty, isUndefined, map, size, uniqBy, intersection, isNull  } from "lodash";
import ProductAPI from "restAPI/ProductAPI";
import SubProductAPI from "restAPI/SubProductAPI";
import {
  BASE_SEVERITIES,
  Environments,
  FINDING_STATUS,
  SEVERITY_NAMES
} from "views/Findings/Constants";
import { CellRenderers } from "utils/tableUtils";
import moment from "moment";
import Approvers from "components/form/Approvers";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import TeamsAPI from "restAPI/TeamsAPI";
import htmlToMarkDown, { sanitizeMarkdown } from "utils/htmlToMarkDownUtil";
import useAPIResponse from "hooks/useAPIResponse";
import ExceptionAPI from "restAPI/ExceptionAPI";
import UserAPI from "restAPI/UserAPI";
import { Row } from "antd";
import SuccessPopup from "components/SuccessPopup";
import ModalUtil from "utils/modalUtil";
import message from "components/CustomMessage";
import useEnabledFindingStatus from "hooks/useEnabledFindingStatus";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { useHasDisabledEnvironment } from "contexts/GlobalSettingsContext";
import { Approvers as ApproversV2, emailValidator } from "./ExceptionDetails/EditApprovalPopupV2/EditApprovalPopupV2";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import useActiveSeveritiesKeys from "hooks/useActiveSeveritiesKeys";
import { useAutoFocus } from "hooks/useFocusHelpers";
import ACCommentEditor from "ac-components/ac-comment-editor/ACCommentEditor";

const initialValues = {};

const DEFAULT_IN_SCOPE_SEVERITY = [SEVERITY_NAMES.critical, SEVERITY_NAMES.high, SEVERITY_NAMES.medium];
const DEFAULT_IN_SCOPE_STATUS = [FINDING_STATUS.OPEN, FINDING_STATUS.TRIAGE, FINDING_STATUS.CONFIRMED];

export default forwardRef(function CreateExceptionFormPopup({ ...props }, ref) {
  const thisRef = useRef();
  const approversRef = useRef([]);
  const [form, setForm] = useState();
  const [formData, setFormData] = useState({});
  const [subProducts, setSubProducts] = useState([]);
  const [createdException, setCreatedException] = useState();
  const validateApiRef = useRef();
  const exceptionInputFieldRef = useAutoFocus();
  const enabledFindingStatus = useEnabledFindingStatus();
  const enabledSeverities = useActiveSeveritiesKeys();
  const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
  const userContext = useActiveUserContext();
  const featureFlags = useFeatureFlag();
  const newApprovalFlow = featureFlags?.EXCEPTION_MODULE_UPDATE;

  useEffect(() => {
    if (formData.product) {
      const { promise, cancel } = SubProductAPI.getAllQuick(formData.product);
      promise.then(setSubProducts, _.noop);
      return cancel;
    }
  }, [formData.product]);

  const [exceptionConfig] = useAPIResponse(() => GlobalSettingAPI.getExceptionConfig(), []);

  const [allUsers, loading] = useAPIResponse(() => {
    const promiseObject = UserAPI.getAll();
    promiseObject.promise = promiseObject.promise
      .then((approvers) => {
        const userMap = map(approvers, (d) => ({
          value: d.id,
          label: d.displayName,
        }));
        return userMap;
      })
      .finally(() => {});
    return promiseObject;
  }, []);

  useImperativeHandle(ref, () => ({ ...thisRef.current }), []);

  const [teamOptions, setTeamOptions] = useState();

  useEffect(() => {
    setTeamOptions();
    if(!formData.product){
      return;
    }
    const { promise, cancel } = TeamsAPI.getProductSubProductTeams({
      productId: formData.product,
      subProductId: formData.subProduct,
    });
    promise.then((teams) => {
      setTeamOptions(() =>
        uniqBy(
          _.map(teams, ({ team }) => ({ value: team.id, label: team.name })),
          "value"
        )
      );
    });
    return cancel;
  }, [formData.product, formData.subProduct]);

  useEffect(() => {
    if (form && formData.autoAddInScopeFinding && formData.product) {
      form.setFieldsValue({
        status: intersection(DEFAULT_IN_SCOPE_STATUS, enabledFindingStatus),
        severity: intersection(DEFAULT_IN_SCOPE_SEVERITY, enabledSeverities)
      });
    }
  }, [form, formData.autoAddInScopeFinding, formData.product, enabledFindingStatus, enabledSeverities]);

  const approversFields = [
    ...(newApprovalFlow ? [] : [{
      name: "space1",
      colSpan: 6,
      type: FieldTypes.LABEL,
    }]),
    {
      name: "teamId",
      label: getVocabulary("team"),
      type: FieldTypes.SELECT,
      options: teamOptions,
      colSpan: 12,
      loading: formData.product && isUndefined(teamOptions),
      disabled: !formData.product,
    },
    {
      name: "space2",
      colSpan: 6,
      type: FieldTypes.LABEL,
    },
    {
      name: "space3",
      colSpan: 6,
      type: FieldTypes.LABEL,
    },
    ...(newApprovalFlow ? 
      [{
        name: "customWorkflow",
        label: "",
        render: () => <ApproversV2 approversRef={approversRef} />,
        colSpan: 24,
        style: { marginBottom: 10 },
        rules: [{
          validator: (rule, values) => emailValidator(rule, values, userContext.sessionData)
        }]
      }] : [{
        name: "customWorkflow",
        label: "",
        render: () => <Approvers approversRef={approversRef} isCustomApprovers={isEmpty(formData.teamId)} />,
        colSpan: 12,
      }] 
    ),
    {
      name: "space4",
      label: "",
      render: () => <div className="m-b-l" />,
      colSpan: 24,
    },
    {
      name: "subscribersUserIds",
      label: "Send Updates To",
      type: FieldTypes.MULTI_SELECT,
      options: allUsers,
      loading: loading
    },
    {
      name: "approveByDate",
      label: "Approve By Date",
      type: FieldTypes.DATE,
      colSpan: 12,
      props: {
        style: { width: "100%" },
      }
    }
  ];

  const pages = [
    {
      name: "Details",
      fields: getBasicFields({ form, validateApiRef, maxExceptionDuration: exceptionConfig?.maxExceptionDurationInDays, exceptionInputFieldRef }),
    },
    {
      name: "Scope",
      fields: getScopeFields({ form, formData, subProducts, setSubProducts, enabledFindingStatus, hasDisabledEnvironment }),
    },
    {
      name: "Approvers",
      fields: approversFields,
    },
  ];

  const onSuccess = useCallback((data) => {
    data?.scope?.autoAddInScopeFinding ?
      ModalUtil.show({
        content: (
          <SuccessPopup
            okText="Done"
          >
            <Row>
                <Vocabulary name={"exception"} /> created with 'Automatically include in-scope findings' enabled,
                the findings may not reflect on this page immediately, please check back after some time.
              </Row>
          </SuccessPopup>
        ),
      }) : message.success(<>Created <Vocabulary name={"exception"} /> {data.name}</>);
    setCreatedException(data);
    thisRef.current.close();
  }, []);

  const onChange = useCallback(
    (values) => {
      const _formData = {};
      if (form) {
        if (_.has(values, "product")) {
          _formData.subProduct = undefined;
          _formData.teamId = undefined;
          _formData.customWorkflow = [];
        } else if (_.has(values, "subProduct")) {
          _formData.teamId = undefined;
          _formData.customWorkflow = [];
        } else if (_.has(values, "customWorkflow")) {
          _formData.teamId = undefined;
          form.setFields([{ name: "teamId", errors: [] }]);
        } else if (_.has(values, "teamId")) {
          _formData.customWorkflow = [];
          if (values.teamId) {
            const { promise } = TeamsAPI.getTeam(values.teamId);
            promise.then((res) => {
              if (size(res.approvalWorkflow?.approvers) > 0) {
                form.setFieldsValue({
                  customWorkflow: _.map(
                    _.sortBy(res.approvalWorkflow?.approvers, ['sequence']),
                    (entry) => entry.reviewerId || entry.externalReviewerEmail
                  ),
                });
              }
            });
          }
        }
      }
      if (!isEmpty(_formData)) {
        form.setFieldsValue(_formData);
      }
      setFormData((formData) => ({ ...formData, ...values, ..._formData }));
    },
    [form]
  );

  function updateDataBeforePush(data) {
    const _data = { ...data };
    if (!_data.teamId && isEmpty(_data.customWorkflow)) {
      const errorJson = {
        response: {
          data: {
            errors: [
              {
                field: "teamId",
                defaultMessage: `Select a ${getVocabulary(
                  "team"
                )} or define approval workflow`,
              },
            ],
          },
        },
      };
      throw errorJson;
    }
    const payload = {
      name: data.name,
      description: sanitizeMarkdown(htmlToMarkDown(data.description)),
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      reasons: data.reasons,
      subscribersUserIds: data.subscribersUserIds,
      approveByDate: data.approveByDate || null
    };
    if (data.product) {
      if(hasDisabledEnvironment){
        data.environmentName = envValue;
      }
      payload.scope = {
        productId: data.product,
        subProductIds: data.subProduct,
        environmentName: data.environmentName,
        autoAddInScopeFinding: data.autoAddInScopeFinding,
        scopeFindingFilters: {
          filters: {
            status: data.status,
            severity: data.severity,
          },
        },
      };
    }
    if (data.teamId) {
      payload.scope = {
        ...payload.scope,
        teamId: data.teamId,
      };
    } else if (!isEmpty(data.customWorkflow)) {
      payload.customWorkflow = data.customWorkflow.map((id, i) => {
        const userData = _.find(approversRef.current, { value: id });
        const isEmail = typeof id === "string" && id.includes("@");
        return ({
          [isEmail ? "externalReviewerEmail" : "reviewerId"]: id,
          reviewerType: isEmail ? "EXTERNAL_USER" : userData?.type || "USER",
          sequence: i,
        });
      });
    }
    return payload;
  }

  return (
    <BaseModal
      modalProps={{
        footer: null,
        width: MODAL_SIZE_MEDIUM,
        style: { top: 80 },
        title: (
          <>
            Create <Vocabulary name="exception" />
          </>
        ),
      }}
      ref={thisRef}
      showExitWarning
      afterClose={() => props.afterClose(createdException)}
    >
      <WizardFormV2
        // isEdit={!_.isUndefined(editId)}
        onCancel={thisRef.current?.close}
        submitButtonText="Save as Draft"
        pages={pages}
        initialValues={initialValues}
        formRef={setForm}
        api={(data) => ExceptionAPI["saveException"](data).promise}
        beforeAPI={updateDataBeforePush}
        onChange={onChange}
        onSuccess={onSuccess}
        validator={parseErrorMessage}
        // loading={editId && _.isEmpty(existingProject)}
        // showSkipSaveBtnFromStep={0}
      />
    </BaseModal>
  );
});

function parseErrorMessage(e) {
  const message = _.lowerCase(e.response?.data?.message ?? "");
  if (message.startsWith("Project with name")) {
    return [
      {
        name: "name",
        errors: [message],
      },
    ];
  }
}

export function getBasicFields({ form, validateApiRef, currentName, maxExceptionDuration, exceptionInputFieldRef }) {

  return [
    {
      name: "name",
      label: (
        <>
          <Vocabulary name="exception" /> Name
        </>
      ),
      colSpan: 12,
      props: {
        ref: exceptionInputFieldRef,
      },
      required: true,
      NoDefaultLengthRule: true,
      rules: [{max: 100 , message: "Maximum length is 100 characters"},
        {min: 3 , message: "Minimum length is 3 characters"},{
          validator: (_, value) => {
            if (value && currentName?.toLowerCase() !== value.toLowerCase()) {
              if (validateApiRef.currect) {
                validateApiRef.current();
              }
              const { promise, cancel } = ExceptionAPI.validate({riskRegisterName:value}, false);
              validateApiRef.current = cancel;
              return promise.catch(() =>
                Promise.reject("Name already exists.")
              );
            }
            return Promise.resolve();
          },
        },
      ],
    },
    {
      name: "description",
      label: <span className="required-mark">Description</span>,
      NoDefaultLengthRule: true,
      valuePropName: "initialValue",
      render: () => {
        return (
        <ACCommentEditor
          placeholder="Description"
          showFooter={false}
        />
        );
      },
      rules: [editorRules()],
    },
    {
      name: "startDate",
      label: "Start date",
      type: FieldTypes.DATE,
      colSpan: 12,
      required: true,
      props: {
        style: { width: "100%" },
        disabledDate: (current) => {
          return (
            current &&
            (current.isAfter(moment().add(2, "years"), "days") ||
              (form?.getFieldValue("endDate") &&
                current.isAfter(form.getFieldValue("endDate"), "day")))
          );
        },
        onChange: (value)=> {
          if(value){
            form.setFields([{
              name: "endDate",
              value: moment(value).add(maxExceptionDuration, "days")
          }])}
      }
      },
    },
    {
      name: "endDate",
      label: "End date",
      type: FieldTypes.DATE,
      required: true,
      props: {
        style: { width: "100%" },
        disabledDate: (current) => {
          return (
            current &&
              (form?.getFieldValue("startDate") &&
                current.isBefore(form.getFieldValue("startDate"), "day"))
          );
        },
      },
      colSpan: 12,
      rules: [
        {
          validator: (_, value) => {
            const startDate = form.getFieldValue("startDate");
            const difference = moment(value).diff(startDate, "day");
            if (value && moment(value).isSame(startDate, "day")) {
              return Promise.reject(
                "The end date cannot be same as start date. Please choose a valid end date."
              );
            } else if (value && difference > maxExceptionDuration) {
              return Promise.reject(
                `The end date cannot be beyond ${maxExceptionDuration} days after the start date. Please choose a valid end date.`
              );
            }
            return Promise.resolve();
          },
        },
      ],
    },
    {
      name: "reasons",
      label: "Reasons",
      type: FieldTypes.MULTI_SELECT,
      props: {
        api: ExceptionAPI.getReasons,
      },
      colSpan: 12,
      help: <>Reasons can be configured from global settings.</>
    },
  ];
}

export const getScopeFields = ({
  form,
  formData,
  subProducts,
  enabledFindingStatus,
  hasDisabledEnvironment=false,
  setSubProducts,
}) => {
  const onProductClear = () => {
    form.setFieldsValue({
      environmentName: undefined,
      subProduct: undefined,
      autoAddInScopeFinding: false,
      status: undefined,
      severity: undefined,
      teamId: undefined,
    });
  };

  const onProductChange = (productID) => {
    form.setFieldsValue({subProduct:undefined})
    setSubProducts(productID ? null : []);
  }

  return [
    {
      name: "product",
      label: <Vocabulary name="product" />,
      type: FieldTypes.SELECT,
      colSpan: 12,
      props: {
        allowClear: true,
        onClear: onProductClear,
        api: ProductAPI.getAllQuick,
        onChange: onProductChange,
      },
    },
    {
      name: "subProduct",
      label: <><Vocabulary name="subProduct" />(s)</>,
      type: FieldTypes.MULTI_SELECT,
      options: subProducts,
      colSpan: 12,
      loading: isNull(subProducts),
    },
    ...(hasDisabledEnvironment ? [] : [{
      name: "environmentName",
      label: "Environment",
      type: FieldTypes.SELECT,
      options: Environments,
      colSpan: 12,
      required: !!formData.product,
    }]),
    {
      type: FieldTypes.LABEL,
      value: <div className="font-size-lg text-bold m-b-xs">Add findings?</div>,
      noStyle: true,
    },
    {
      name: "autoAddInScopeFinding",
      label: "Automatically include in-scope findings?",
      type: FieldTypes.BOOLEAN,
      inline: true,
      props: {
        disabled: !formData.product,
      },
    },
    ...(formData.autoAddInScopeFinding && formData.product
      ? [
          {
            name: "status",
            label: "Only include if status is:",
            type: FieldTypes.MULTI_SELECT,
            colSpan: 12,
            options: enabledFindingStatus.map(
              (key) => ({
                label: CellRenderers.VOCABULARY_RENDERER(key),
                value: key,
              })
            ),
          },
          {
            name: "severity",
            label: "Only include if severity is:",
            type: FieldTypes.MULTI_SELECT,
            colSpan: 12,
            options: BASE_SEVERITIES.map((severity) => ({
              label: getVocabulary(severity),
              value: severity,
            })),
          },
        ]
      : []),
  ];
};